import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbPlus } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PageHeader from '../../components/misc/PageHeader';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import * as bankAccountService from '../../../services/community/bankAccountService';
import {
  BankAccount,
  GoCardlessInstitution,
} from '../../../types/bankAccount';
import ModalLayout from '../../components/layouts/ModalLayout';
import Button from '../../components/buttons/Button';
import Labeled from '../../components/misc/Labeled';
import BankAccountsCard from '../../components/cards/BankAccountsCard';
import DatePicker from '../../components/forms/DatePicker';
import CreateBankAccountModal from '../../components/modals/CreateBankAccountModal';

interface BankAccountsPageProps {
  hideHeader?: boolean;
}

interface BankAccountWithDetails extends BankAccount {}

export default function BankAccountsPage({
  hideHeader = false,
}: BankAccountsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.accountEntry' });
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccountWithDetails[]>();
  const [createAccountModalOpen, setCreateAccountModalOpen] = useState<boolean>(false);
  const [cardlessModalOpen, setCardlessModalOpen] = useState<boolean>(false);
  const [selectedAccountIdForLink, setSelectedAccountIdForLink] = useState<number | null>(null);
  const [unprocessedByAccount, setUnprocessedByAccount] = useState<Record<number, number>>({});

  const community = useSelectedCommunity();
  const navigate = useNavigate();

  useEffect(() => {
    loadBankAccounts();
  }, [community]);

  const loadBankAccounts = async () => {
    try {
      const accounts = await bankAccountService.getAllBankAccounts();
      setAllBankAccounts(accounts);
      // load unprocessed counts per account
      const counts = await Promise.all(
        accounts.map(async (acc) => {
          try {
            const mutations = await bankAccountService.getAllMutationsForBank(acc.accountId);
            const unprocessed = mutations.filter((m) => m.journalEntryCount === 0).length;
            return [acc.accountId, unprocessed] as const;
          } catch {
            return [acc.accountId, 0] as const;
          }
        }),
      );
      setUnprocessedByAccount(Object.fromEntries(counts));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      toast.error('Failed to load bank accounts');
    }
  };

  const handleAccountClick = (accountId: number) => {
    navigate(`/financial/mutations/${accountId}`);
  };

  const connectedAccounts = (allBankAccounts ?? []).filter(
    (acc) => acc.requisitionId && acc.transferCompleted,
  ).length;
  const disconnectedAccounts = (allBankAccounts?.length ?? 0) - connectedAccounts;

  // No per-account helpers needed; the card handles statuses and labels

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title="Bank Accounts"
          subtitle="Manage your connected bank accounts and view account details"
          primaryButton={
            <Button variant="primary-dark" onClick={() => setCreateAccountModalOpen(true)}>
              <TbPlus />
              Add Bank Account
            </Button>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-1">
        <BankAccountsCard
          bankAccounts={allBankAccounts ?? []}
          metrics={{ connectedAccounts, disconnectedAccounts }}
          unprocessedByAccount={unprocessedByAccount}
          onCreateAccount={() => setCreateAccountModalOpen(true)}
          onAccountClick={handleAccountClick}
          onStartSync={async (accountId) => {
            const account = (allBankAccounts ?? []).find((a) => a.accountId === accountId);
            if (!account) return;
            const needsReauth =
              account.autoFetchDisabledReason === 'GC_ACCESS_EXPIRED' ||
              (!account.autoFetchDisabledReason &&
                account.autoFetchEnabled === false &&
                !!account.goCardlessAccountId);

            if (needsReauth) {
              if (!account.requisitionId) {
                toast.error(t('error.createGoCardlessLink'));
                return;
              }

              try {
                const gcAccounts = await bankAccountService.getAccountsByRequisition(
                  account.requisitionId,
                );
                const gcAccount =
                  (account.goCardlessAccountId &&
                    gcAccounts.find((a) => a.id === account.goCardlessAccountId)) ||
                  gcAccounts[0];
                if (!gcAccount) {
                  throw new Error('NO_GC_ACCOUNT');
                }

                const linkData = await bankAccountService.createGoCardlessLink(
                  accountId,
                  gcAccount.institution_id,
                  i18n.language as 'en' | 'nl',
                );
                const a = document.createElement('a');
                a.href = linkData.link;
                a.click();
                a.remove();
              } catch (err) {
                console.error('Error creating GoCardless link for reauth:', err);
                toast.error(t('error.createGoCardlessLink'));
              }
              return;
            }

            if (!account.goCardlessAccountId) {
              setSelectedAccountIdForLink(accountId);
              setCardlessModalOpen(true);
              return;
            }

            try {
              await bankAccountService.fetchLatestGoCardlessTransactions(accountId);
              toast.success(t('success.refetchMutations'));
              // Refresh mutations for this account to update unprocessed count
              try {
                const mutations = await bankAccountService.getAllMutationsForBank(accountId);
                const unprocessed = mutations.filter((m) => m.journalEntryCount === 0).length;
                setUnprocessedByAccount((prev) => ({ ...prev, [accountId]: unprocessed }));
              } finally {
                // also reload accounts to reflect balances etc.
                loadBankAccounts();
              }
            } catch {
              toast.error(t('error.refetchMutations'));
            }
          }}
        />
      </div>

      <CreateBankAccountModal
        open={createAccountModalOpen}
        setOpen={setCreateAccountModalOpen}
        onSuccess={(newData) => {
          setCreateAccountModalOpen(false);
          bankAccountService
            .createBankAccount(newData)
            .then((ba) => {
              setAllBankAccounts((bas) => (bas ? [...bas, ba] : [ba]));
              toast.success('Bank account created successfully');
            })
            .catch(() => {
              toast.error(t('error.createBankAccount'));
            });
        }}
      />

      <LinkBankAccountModal
        open={cardlessModalOpen}
        setOpen={setCardlessModalOpen}
        onBankChosen={async (bankId, fromDate) => {
          if (!selectedAccountIdForLink) return;
          try {
            if (fromDate) {
              localStorage.setItem(`gc_fromDate_${selectedAccountIdForLink}`, fromDate);
            }
            const linkData = await bankAccountService.createGoCardlessLink(
              selectedAccountIdForLink,
              bankId,
              i18n.language as 'en' | 'nl',
            );
            const a = document.createElement('a');
            a.href = linkData.link;
            a.click();
            a.remove();
          } catch (err) {
            console.error('Error creating GoCardless link:', err);
            toast.error(t('error.createGoCardlessLink'));
          }
        }}
      />
    </>
  );
}

function LinkBankAccountModal({
  open,
  setOpen,
  onBankChosen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onBankChosen: (bankId: string, fromDate?: string) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.accountEntry.linkBankModal' });
  const [institutions, setInstitutions] =
    useState<Array<{ id: string; name: string; logo: string; transaction_total_days?: number }>>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<{
    id: string;
    name: string;
    logo: string;
    transaction_total_days?: number;
  } | null>(null);
  const [startDate, setStartDate] = useState<Date>();

  useEffect(() => {
    if (open && !institutions) {
      bankAccountService.getDutchBanks().then((banks) => {
        const mapped = banks.map((b: GoCardlessInstitution) => ({
          id: b.id,
          name: b.name,
          logo: b.logo,
          transaction_total_days: b.transaction_total_days ? +b.transaction_total_days : undefined,
        }));
        setInstitutions(mapped);
      });
    }
  }, [open, institutions]);

  const handleBankChosen = (instId: string, fromDate?: string) => {
    setIsLoading(true);
    setTimeout(() => {
      onBankChosen(instId, fromDate).finally(() => setIsLoading(false));
    });
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
      <div>
        <h2 className="mb-4 text-lg font-semibold">{t('selectBank')}</h2>
        <div className="flex max-h-[calc(100vh-20rem)] flex-col gap-2 overflow-y-auto">
          {institutions && !isLoading ? (
            <>
              {institutions.map((inst) => (
                <button
                  key={inst.id}
                  className={`mb-2 flex w-full items-center rounded-md border-2 border-secondary-200 ${
                    inst.id === selectedBank?.id ? 'bg-secondary-200' : 'bg-secondary-50'
                  } p-4 hover:bg-secondary-200`}
                  type="button"
                  onClick={() => setSelectedBank(inst)}>
                  <div className="flex items-center gap-2">
                    <img
                      src={inst.logo}
                      alt={`${inst.name} logo`}
                      className="h-8 w-8 object-contain"
                    />
                    <p>{inst.name}</p>
                  </div>
                </button>
              ))}
              {selectedBank && (
                <Labeled label={t('startDay')}>
                  <DatePicker
                    date={startDate}
                    setDate={setStartDate}
                    minDate={dayjs().subtract(
                      selectedBank.transaction_total_days
                        ? +selectedBank.transaction_total_days
                        : 90,
                      'days',
                    )}
                    maxDate={dayjs()}
                    includeTime={false}
                  />
                  <p className="text-sm text-gray-500">{t('startDayHelper')}</p>
                </Labeled>
              )}
              <Button
                variant="primary-dark"
                className="mt-4 self-end"
                disabled={!selectedBank}
                onClick={() =>
                  handleBankChosen(
                    selectedBank!.id,
                    startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
                  )
                }>
                {t('linkButton')}
              </Button>
            </>
          ) : (
            <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-b-2" />
          )}
        </div>
      </div>
    </ModalLayout>
  );
}
